import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import request from '@/utils/request';

interface DataPoint {
  date: string;
  count: number;
}

interface UseAnalyticsReturn {
  userGrowth: DataPoint[];
  dau: DataPoint[];
  imageUploads: DataPoint[];
  loading: boolean;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  setDateRange: (range: [dayjs.Dayjs, dayjs.Dayjs]) => void;
}

export function useAnalytics(): UseAnalyticsReturn {
  const [userGrowth, setUserGrowth] = useState<DataPoint[]>([]);
  const [dau, setDau] = useState<DataPoint[]>([]);
  const [imageUploads, setImageUploads] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  const parseData = (data: unknown): DataPoint[] => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'data' in data) {
      return Array.isArray((data as { data: unknown }).data)
        ? (data as { data: DataPoint[] }).data
        : [];
    }
    return [];
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const days = dateRange[1].diff(dateRange[0], 'day') + 1;

    try {
      const [growthRes, dauRes, imagesRes] = await Promise.allSettled([
        request.get('/admin/analytics/user-growth', { params: { days } }),
        request.get('/admin/analytics/dau', { params: { days } }),
        request.get('/admin/analytics/image-uploads', { params: { days } }),
      ]);

      setUserGrowth(
        growthRes.status === 'fulfilled' ? parseData(growthRes.value.data) : [],
      );
      setDau(
        dauRes.status === 'fulfilled' ? parseData(dauRes.value.data) : [],
      );
      setImageUploads(
        imagesRes.status === 'fulfilled'
          ? parseData(imagesRes.value.data)
          : [],
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    userGrowth,
    dau,
    imageUploads,
    loading,
    dateRange,
    setDateRange,
  };
}
